import React, { forwardRef } from 'react';
import type { ElementType } from 'react';
import { StyledGrid } from './HBGrid.styles';
import {
    buildBaseCss,
    buildSpacingCss,
    buildSizingCss,
    buildChildCss,
    pickBaseProps,
    pickSpacingProps,
    pickSizingProps,
    pickChildProps,
    responsiveSpacingCss,
    responsiveValueCss,
    withDisplayName
} from '../../styles/layout/layout.utils';
import type { HBGridProps } from './HBGrid.types';
import type {
    StrictPolymorphicProps,
    PolymorphicRef
} from '../../styles/layout/layout.types';

type HBGridPolymorphicProps<C extends ElementType = 'div'> =
    StrictPolymorphicProps<C, HBGridProps>;

type HBGridComponent = <C extends ElementType = 'div'>(
    props: HBGridPolymorphicProps<C> & { ref?: PolymorphicRef<C> }
) => React.ReactElement | null;

function buildGridCss(props: HBGridProps) {
    return [
        ...buildBaseCss(props),
        ...buildSpacingCss(props),
        ...buildSizingCss(props),
        ...buildChildCss(props),
        responsiveValueCss(props.display ?? 'grid', ['display']),
        responsiveValueCss(props.columns, ['grid-template-columns']),
        responsiveValueCss(props.rows, ['grid-template-rows']),
        responsiveSpacingCss(props.gap, ['gap']),
        ...(props.gap
            ? []
            : [
                  responsiveSpacingCss(props.gapX, ['column-gap']),
                  responsiveSpacingCss(props.gapY, ['row-gap'])
              ])
    ];
}

/**
 * CSS Grid layout container. Accepts columns, rows, and gap props.
 * Supports responsive breakpoint objects for adaptive grid layouts.
 */
const HBGrid = forwardRef(
    <C extends ElementType = 'div'>(
        {
            as,
            children,
            display,
            columns,
            rows,
            gap,
            gapX,
            gapY,
            displayName,
            ...allProps
        }: HBGridPolymorphicProps<C>,
        ref: PolymorphicRef<C>
    ) => {
        const [baseProps, afterBase] = pickBaseProps(allProps);
        const [spacingProps, afterSpacing] = pickSpacingProps(afterBase);
        const [sizingProps, afterSizing] = pickSizingProps(afterSpacing);
        const [childProps, afterChild] = pickChildProps(afterSizing);
        // Defense in depth: `className` and `style` are omitted from
        // HBGridProps, but styled-components still injects a runtime
        // `className` prop when this component is wrapped via
        // `styled(HBGrid)`, and a consumer could still pass `style` via
        // `as any`/JS, regardless of the declared type. Strip both explicitly
        // so neither has any visual effect.
        const {
            className: _discardedClassName,
            style: _discardedStyle,
            ...rest
        } = afterChild as typeof afterChild & {
            className?: string;
            style?: React.CSSProperties;
        };
        const layoutCss = buildGridCss({
            display,
            columns,
            rows,
            gap,
            gapX,
            gapY,
            ...baseProps,
            ...spacingProps,
            ...sizingProps,
            ...childProps
        });

        return withDisplayName(
            displayName,
            <StyledGrid
                as={as || 'div'}
                ref={ref}
                $layoutCss={layoutCss}
                data-component-name={displayName}
                {...rest}
            >
                {children}
            </StyledGrid>
        );
    }
) as HBGridComponent;

(HBGrid as React.FC).displayName = 'HBGrid';

export default HBGrid;
